import SpeedOutlined from '@mui/icons-material/SpeedOutlined';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { type ReactNode, useMemo } from 'react';
import { type PanelValues, useFilterCatalog } from '../../lib/filters.hooks';
import { FilterMenu, type FilterMenuEntry, type FilterMenuFamily } from './FilterMenu';
import {
  type AnyFilterKey,
  FILTER_CATALOG,
  filterDefinition,
  isCustomFilterKey,
} from './filterCatalog';
import { FILTER_FAMILIES, FILTER_FAMILY_ORDER } from './filterFamilies';
import { filterMenuItemSx } from './menuStyles';

interface AddFilterMenuProps {
  activeKeys: Set<AnyFilterKey>;
  metricAdded: boolean;
  showMetric?: boolean;
  scopeNetworks: string[];
  networkLabels: Record<string, string>;
  panelValues: PanelValues | undefined;
  accountIds: number[];
  /** Off where the filters are handed to the actions service, which has no field for a
   * tenant-declared dimension and would drop it. */
  offerTenantFilters?: boolean;
  onAddMetric: () => void;
  onAddFilter: (key: AnyFilterKey) => void;
}

const FAMILIES: FilterMenuFamily[] = FILTER_FAMILY_ORDER.map((familyId) => ({
  id: familyId,
  label: FILTER_FAMILIES[familyId].label,
  tone: FILTER_FAMILIES[familyId].tone,
  badge: FILTER_FAMILIES[familyId].matchOperator,
}));

const CATALOG_ENTRIES: FilterMenuEntry[] = FILTER_CATALOG.map((definition) => ({
  key: definition.key,
  label: definition.label,
  icon: definition.icon,
  family: definition.family,
}));

export function AddFilterMenu({
  activeKeys,
  metricAdded,
  showMetric = true,
  scopeNetworks,
  networkLabels,
  panelValues,
  accountIds,
  offerTenantFilters = false,
  onAddMetric,
  onAddFilter,
}: AddFilterMenuProps): ReactNode {
  const { data: catalog } = useFilterCatalog(accountIds);

  const entries = useMemo((): FilterMenuEntry[] => {
    if (!offerTenantFilters) return CATALOG_ENTRIES;
    const tenantEntries = (catalog ?? []).flatMap((entry): FilterMenuEntry[] => {
      if (!isCustomFilterKey(entry.key)) return [];
      const definition = filterDefinition(entry.key, entry.label);
      return [
        {
          key: entry.key,
          label: definition.label,
          icon: definition.icon,
          family: definition.family,
        },
      ];
    });
    return [...CATALOG_ENTRIES, ...tenantEntries];
  }, [catalog, offerTenantFilters]);

  const unsupportedNetworksByKey = useMemo(
    () =>
      new Map<string, string[]>(
        (catalog ?? []).map((entry) => [entry.key, entry.unsupportedNetworks]),
      ),
    [catalog],
  );

  const label = (network: string): string => networkLabels[network] ?? network;
  const breakdownTaken = [...activeKeys].some(
    (key) => filterDefinition(key).family === 'breakdown',
  );

  const disabledReason = (rawKey: string): string | null => {
    const key = rawKey as AnyFilterKey;
    const definition = filterDefinition(key);
    const unsupported = unsupportedNetworksByKey.get(key) ?? [];
    const affected = scopeNetworks.filter((network) => unsupported.includes(network));
    if (affected.length > 0) {
      const [only] = affected;
      return affected.length === 1 && scopeNetworks.length === 1 && only
        ? `This filter isn't available for ${label(only)}`
        : `Not available for platforms: ${affected.map(label).join(', ')}`;
    }
    const dependsOn = definition.dependsOn;
    if (dependsOn && !activeKeys.has(dependsOn)) {
      return `Add the ${filterDefinition(dependsOn).label} filter first`;
    }
    const excluded =
      (breakdownTaken && definition.family === 'breakdown' && !activeKeys.has(key)) ||
      panelValues?.[key]?.unavailable;
    return excluded ? 'Only one performance breakdown can be filtered at a time' : null;
  };

  const metricItem = (close: () => void): ReactNode => (
    <MenuItem
      disabled={metricAdded}
      onClick={() => {
        onAddMetric();
        close();
      }}
      sx={filterMenuItemSx}
    >
      <ListItemIcon>
        <SpeedOutlined fontSize="small" />
      </ListItemIcon>
      <ListItemText primary="Metric" secondary="ROAS, CTR, CPA… condition builder" />
      {metricAdded ? (
        <Typography variant="caption" color="text.disabled">
          Added
        </Typography>
      ) : null}
    </MenuItem>
  );

  return (
    <FilterMenu
      families={FAMILIES}
      entries={entries}
      activeKeys={activeKeys}
      disabledReason={disabledReason}
      onAdd={(key) => onAddFilter(key as AnyFilterKey)}
      {...(showMetric ? { leadingItem: metricItem } : {})}
    />
  );
}
